/**
 * Initial page
 */
import Focus from '@shared/types/enums/focus';
import FocusUtils from '@shared/utils/focus';
import Main from '@shared/types/enums/main';
import Rail from '@shared/classes/rail';
import Slider from '@shared/classes/slider';
import TMain from '@components/templates/main';
import initialObservable from '@shared/api/initial/initialObservable';
import keepWatchingRequest from '@shared/api/initial/keepWatchingRequest';
import recommendationsRequest from '@shared/api/recommendations/request';
import useComplete from '@shared/components/utils/hooks/useComplete';
import { Observer, Subscription } from 'rxjs';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { eventsSelector } from '@shared/store/slices/events.slice';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { planSelector } from '@shared/store/slices/plans.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import { useAppSelector } from '@shared/store/hooks';
import { userSelector } from '@shared/store/slices/user.slice';
import { useState, useEffect, useRef, useMemo } from 'react';

// GraphQL
import useSection from '@shared/components/utils/hooks/useSection';
import { QuerySections } from '@shared/types/enums/sections';

// Constants
const KEEP_WATCHING_POSITION_INDEX = 2;
const RECOMMENDATIONS_POSITION_INDEX = 3;

/**
 * Displays the initial page with the keep watching, recommendations and media reels
 */
export default function Initial() {
  //--------------------------------
  // CDN SYNC
  //--------------------------------

  // Selectors
  const { channelsData } = useAppSelector(channelsSelector);
  const { eventsData, mockedEvents } = useAppSelector(eventsSelector);
  const { data: planData } = useAppSelector(planSelector);
  const { initialized } = useAppSelector(userSelector);
  const { focus } = useAppSelector(focusSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);

  // State
  const [complete, setComplete] = useComplete();
  const [entered, setEntered] = useState(false);
  const [fetch, setFetch] = useState(complete);
  const [rails, setRails] = useState<Rail[]>(
    (currentParams as { rails: Rail[] })?.rails || []
  );
  const [sliders, setSliders] = useState<Slider[]>(
    (currentParams as { sliders: Slider[] })?.sliders || []
  );

  // Refs
  const railsRef = useRef<Rail[]>([]);
  railsRef.current = rails;

  // Memoized values
  const load = useMemo(() => {
    if (complete) return false;
    if (currentParams?.rails) {
      setComplete(true);
      return false;
    }
    return (
      channelsData?.length &&
      planData?.length &&
      (Object.keys(eventsData).length || Object.keys(mockedEvents).length) &&
      initialized
    );
  }, [
    channelsData,
    eventsData,
    mockedEvents,
    planData,
    complete,
    currentParams,
    initialized,
  ]);

  //--------------------------------
  // GraphQL
  //--------------------------------

  const { reels, onPagination } = useSection({
    ready: fetch,
    section: QuerySections.INITIAL,
  });

  //--------------------------------
  // Actions
  //--------------------------------

  const populateRails = (currentRails: Rail[], newRails: Rail[]) => {
    const rails: Rail[] = [];
    const set = new Set<string>();
    [...currentRails, ...newRails].forEach((rail) => {
      if (!set.has(rail.id)) {
        set.add(rail.id);
        rails.push(rail);
      }
    });
    return rails;
  };

  const populateOnMovement = () => {
    const { keepWatching, user } = store.getState();
    const loadKeepWatching =
      !keepWatching.data.length &&
      !keepWatching.initialized &&
      !user.isAnonymous;
    const loadRecommendations = !user.isAnonymous;
    if (loadKeepWatching || loadRecommendations) {
      const requests = [];
      if (loadKeepWatching) {
        requests.push(keepWatchingRequest());
      }
      if (loadRecommendations) {
        requests.push(recommendationsRequest());
      }
      Promise.all(requests)
        .then((val) => {
          let keepWatchingRail: Rail | undefined;
          let recommendationsRail: Rail | undefined;
          // If keep watching data is available, add it to the rails
          if (loadKeepWatching) {
            keepWatchingRail = val[0] as Rail;
          }
          // If recommendations data is available, add it to the rails
          if (loadRecommendations) {
            recommendationsRail = val[loadKeepWatching ? 1 : 0] as Rail;
          }

          const data: Rail[] = [];
          railsRef.current.forEach((item, listIndex) => {
            if (!item.dummy) {
              data.push(item);
            }
            if (
              listIndex === KEEP_WATCHING_POSITION_INDEX &&
              keepWatchingRail
            ) {
              data.push(keepWatchingRail);
            }
            if (
              listIndex === RECOMMENDATIONS_POSITION_INDEX &&
              recommendationsRail
            ) {
              data.push(recommendationsRail);
            }
          });
          setRails(data);
        })
        .finally(() => {
          setFetch(true);
        });
    } else {
      setFetch(true);
    }
  };

  //--------------------------------
  // Effect hooks
  //--------------------------------

  useEffect(() => {
    let subscription: Subscription;
    if (load) {
      const initialObserver: Observer<{
        data: Slider[] | Rail[];
        type: Main;
      }> = {
        next: (val) => {
          if (val.type === Main.SLIDERS) {
            setSliders(val.data as Slider[]);
          } else if (val.type === Main.RAILS) {
            setRails(val.data as Rail[]);
          }
        },
        error: () => {
          console.error('Error recovering initial page data');
        },
        complete: () => {
          setComplete(true);
          return null;
        },
      };
      subscription = initialObservable().subscribe(
        initialObserver as Partial<Observer<unknown>>
      );
    }
    return () => {
      subscription?.unsubscribe();
    };
  }, [load]);

  useEffect(() => {
    // Populate keep watching and media reels when page is focused (just once)
    const hasFocus =
      FocusUtils.evaluateSpecificFocus(Focus.RAILS_LIST, focus as number[]) ||
      focus === Focus.SLIDERS;
    const shouldLoad = !entered && hasFocus;

    // If page is focused and fetch has not been completed, populate keep watching and media reels
    if (shouldLoad) {
      setEntered(true);
      populateOnMovement();
    }
  }, [focus, entered]);

  useEffect(() => {
    setRails(populateRails(rails, reels));
  }, [reels]);

  //--------------------------------
  // Render
  //--------------------------------

  return (
    <TMain
      complete={complete}
      rails={rails}
      hideBackground={false}
      sliders={sliders}
      startsInMenu
      startsInSlider
      onPagination={onPagination}
      hasFilters={true}
    />
  );
}
